import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Repeat, Play, Pause, Trash2, ShoppingCart, Calendar, Clock } from 'lucide-react';
import axios from 'axios';

const API = window.location.origin + '/api';
const FREQ_LABELS = { weekly: 'Every Week', biweekly: 'Every 2 Weeks', monthly: 'Every Month' };
const SLOT_LABELS = { morning: '8AM-12PM', afternoon: '12PM-4PM', evening: '4PM-8PM' };

export default function Subscriptions() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordering, setOrdering] = useState(null);
  const navigate = useNavigate();

  const fetchSubs = async () => {
    try {
      const { data } = await axios.get(`${API}/recurring-orders`, { withCredentials: true });
      setSubs(data);
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchSubs(); }, []);

  const togglePause = async (sub) => {
    try {
      await axios.put(`${API}/recurring-orders/${sub.id}`, { active: !sub.active }, { withCredentials: true });
      fetchSubs();
    } catch (err) {
      console.error('Failed to toggle subscription:', err);
      alert('Could not update subscription');
    }
  };

  const handleOrderNow = async (subId) => {
    setOrdering(subId);
    try {
      await axios.post(`${API}/recurring-orders/${subId}/order-now`, {}, { withCredentials: true });
      fetchSubs();
      alert('Order placed successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to place order');
    }
    setOrdering(null);
  };

  const handleDelete = async (subId) => {
    if (!window.confirm('Cancel this recurring order? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/recurring-orders/${subId}`, { withCredentials: true });
      fetchSubs();
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      alert('Could not cancel subscription');
    }
  };

  const getTotal = (items) => items.reduce((sum, i) => sum + (i.price || 0) * (i.quantity || 1), 0);

  return (
    <div className="orders-page" data-testid="subscriptions-page">
      <button className="back-btn" onClick={() => navigate('/')}>
        <ArrowLeft /> Home
      </button>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Repeat size={24} /> My Recurring Orders
      </h2>

      {loading ? <div className="loading-screen">Loading...</div> :
        subs.length === 0 ? (
          <div className="empty-state">
            <Repeat size={64} />
            <h3>No recurring orders yet</h3>
            <p>Place an order and choose "Make it recurring" to set up automatic deliveries</p>
            <button className="btn-lime" onClick={() => navigate('/orders')} style={{ maxWidth: '200px', margin: '0 auto' }}>View My Orders</button>
          </div>
        ) : (
          subs.map(sub => (
            <div className="order-card" key={sub.id} data-testid={`sub-card-${sub.id}`} style={{ borderLeft: `4px solid ${sub.active ? 'var(--lime)' : 'var(--border-gray)'}` }}>
              <div className="order-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Repeat size={16} style={{ color: sub.active ? 'var(--lime)' : 'var(--text-muted)' }} />
                  <span className="order-id" style={{ fontSize: '0.85rem' }}>
                    {FREQ_LABELS[sub.frequency] || sub.frequency} &bull; {sub.preferred_day.charAt(0).toUpperCase() + sub.preferred_day.slice(1)}s
                  </span>
                </div>
                <span className={`order-status ${sub.active ? 'status-confirmed' : 'status-cancelled'}`}>
                  {sub.active ? 'Active' : 'Paused'}
                </span>
              </div>

              {sub.supermarket_name && (
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '6px' }}>
                  Store: <strong>{sub.supermarket_name}</strong>
                </div>
              )}

              <div className="order-items-list" style={{ marginBottom: '8px' }}>
                {sub.items.map((item, i) => (
                  <span key={`${sub.id}-${item.product_id || item.name}-${i}`}>{item.name} x{item.quantity}{i < sub.items.length - 1 ? ', ' : ''}</span>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', fontSize: '0.82rem', marginBottom: '8px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--navy)' }}>
                  <Calendar size={14} /> Next: {sub.next_delivery_date || 'N/A'}
                </span>
                {sub.delivery_time_slot && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--text-muted)' }}>
                    <Clock size={14} /> {SLOT_LABELS[sub.delivery_time_slot] || sub.delivery_time_slot}
                  </span>
                )}
                <span className="order-total">{getTotal(sub.items).toFixed(2)} PGK</span>
              </div>

              {sub.total_orders_placed > 0 && (
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  {sub.total_orders_placed} order{sub.total_orders_placed > 1 ? 's' : ''} placed so far
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
                <button
                  onClick={() => handleOrderNow(sub.id)}
                  disabled={!sub.active || ordering === sub.id}
                  data-testid={`order-now-${sub.id}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 14px',
                    borderRadius: '8px', border: 'none', background: 'var(--lime)', color: 'white',
                    fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    opacity: sub.active ? 1 : 0.5
                  }}
                >
                  <ShoppingCart size={14} /> {ordering === sub.id ? 'Placing...' : 'Order Now'}
                </button>
                <button
                  onClick={() => togglePause(sub)}
                  data-testid={`toggle-${sub.id}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 14px',
                    borderRadius: '8px', border: '1px solid var(--border-gray)', background: 'var(--white)',
                    color: 'var(--text-dark)', fontSize: '0.82rem', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit'
                  }}
                >
                  {sub.active ? <><Pause size={14} /> Pause</> : <><Play size={14} /> Resume</>}
                </button>
                <button
                  onClick={() => handleDelete(sub.id)}
                  data-testid={`cancel-${sub.id}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 14px',
                    borderRadius: '8px', border: '1px solid var(--border-gray)', background: 'var(--white)',
                    color: 'var(--danger)', fontSize: '0.82rem', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit'
                  }}
                >
                  <Trash2 size={14} /> Cancel
                </button>
              </div>
            </div>
          ))
        )
      }
    </div>
  );
}
